import type { DeploymentConfig } from './types';

const PROD_SERVER_URL = 'https://qodo-platform.qodo.ai';

/**
 * The backend base URL, resolved synchronously at module-eval time so eager
 * consumers (http client BASE_URL consts) always have a usable value:
 *  1. window.__SERVER_URL__ — written to /config.js by
 *     docker-entrypoint.d/40-runtime-config.sh on managed/on-prem deployments
 *  2. VITE_BACKEND_URL — local dev / PR previews (`start:*` scripts)
 *  3. Default — production backend on production builds (CDN deployments
 *     inject nothing), localhost backend on dev/test builds
 */
// || not ?? — the Dockerfile bakes VITE_BACKEND_URL="" when the build-arg is
// unset, and an empty string must fall through to the default.
export const SERVER_URL: string = (
  window.__SERVER_URL__ ||
  (import.meta.env.VITE_BACKEND_URL as string | undefined) ||
  (import.meta.env.PROD ? PROD_SERVER_URL : 'http://localhost:8000')
).replace(/\/+$/, '');

/** Which path produced the cached config. */
export type ConfigSource = 'backend' | 'fallback' | 'test';

const normalizeConfig = (config: DeploymentConfig): DeploymentConfig => ({
  ...config,
  parent_domain: config.parent_domain?.replace(/^\.+/, ''),
});

let cached: DeploymentConfig | undefined;
let cacheIsFinal = false;
let resolvedSource: ConfigSource | undefined;

export const getResolvedConfigSource = (): ConfigSource | undefined =>
  resolvedSource;

// Placeholder for eager module-eval reads (only server_url is consumed before
// the bootstrap awaits loadDeploymentConfig) and for dev without a backend.
const fallbackConfig = (): DeploymentConfig => ({
  auth_client_type: 'oidc',
  server_url: SERVER_URL,
  deployment_type: 'full',
  features: {
    git_provider_linking: true,
    mt_wizard_enabled: true,
    onboarding_wizard_available: true,
  },
  internal_org_access_enabled: true,
});

const finalize = (
  config: DeploymentConfig,
  source: ConfigSource,
  isFinal: boolean,
): DeploymentConfig => {
  cached = normalizeConfig(config);
  resolvedSource = source;
  cacheIsFinal = isFinal;

  if (import.meta.env.MODE !== 'test') {
    console.info(
      `[config] resolved from "${source}": server_url=${cached.server_url}, ` +
        `auth=${cached.auth_client_type}, deployment_type=${cached.deployment_type}`,
    );
  }

  return cached;
};

/**
 * Fetches the DeploymentConfig from the backend. Call once at app startup and
 * await it before rendering. On production builds a fetch failure throws — the
 * bootstrap renders a fatal screen instead of running against a half-config.
 * On dev/test builds it falls back to a local default so pure frontend dev
 * works without a running backend.
 */
export const loadDeploymentConfig = async (): Promise<DeploymentConfig> => {
  // A provisional cache (set by an eager sync read below) must not block the
  // backend fetch, otherwise the app is stranded on the fallback.
  if (cached && cacheIsFinal) return cached;

  try {
    const res = await fetch(`${SERVER_URL}/config/v1/deployment-config`, {
      signal: AbortSignal.timeout(5000),
    });

    if (!res.ok) {
      throw new Error(
        `deployment-config fetch failed: ${res.status} from ${SERVER_URL}`,
      );
    }

    return finalize((await res.json()) as DeploymentConfig, 'backend', true);
  } catch (error) {
    if (import.meta.env.PROD) throw error;

    console.warn(
      `[config] failed to fetch deployment config from ${SERVER_URL} — using dev fallback`,
      error,
    );

    return finalize(fallbackConfig(), 'fallback', true);
  }
};

/**
 * Returns the cached DeploymentConfig synchronously. Before
 * loadDeploymentConfig() resolves, returns a provisional fallback whose
 * server_url is correct (SERVER_URL is sync-resolvable) — the only field
 * consumed at module-eval time.
 */
export const getDeploymentConfig = (): DeploymentConfig => {
  if (!cached) return finalize(fallbackConfig(), 'fallback', false);

  return cached;
};

/**
 * Synchronously sets the cached config. For use in tests only.
 * Bypasses the backend fetch so tests can render ConfigProvider directly.
 */
export const _setConfigForTest = (config: DeploymentConfig): void => {
  cached = config;
  resolvedSource = config ? 'test' : undefined;
  cacheIsFinal = !!config;
};
